import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Box, Stack, Typography, Paper, Chip, Button, CircularProgress, Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CreateApi from './Settings/appSettings';
import { StyledTextboxInput } from './Components/StyledTextboxInput';
import { StyledAutocompleteInput } from './Components/StyledAutocompleteInput';
import { useMCContext } from './contexts/useMCStates';
import stateAbbreviations from './Settings/StateAbbreviations.json';
import type { MomentUser } from '@moment-golf/types';

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

function hasGhinValue(ghin: unknown): boolean {
    return Boolean((ghin ?? '').toString().trim());
}

/** Deep clone MomentUser-ish record from API JSON for drafting. */
function cloneUser(src: MomentUser): MomentUser {
    return { ...src, tours: src.tours ? [...src.tours] : undefined };
}

const ReadOnlyField = ({ label, value, factor }: { label: string; value: string; factor: number }) => (
    <Stack direction="column" spacing={0.25} sx={{ minWidth: 160, alignItems: 'flex-start', alignSelf: 'flex-start' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 * factor, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left', width: '100%' }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 12 * factor, fontWeight: 500, textAlign: 'left', width: '100%' }}>
            {value || '—'}
        </Typography>
    </Stack>
);

const SectionLabel = ({ label, factor }: { label: string; factor: number }) => (
    <Typography
        variant="caption"
        color="primary.main"
        sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1,
            fontSize: 10 * factor,
            width: '100%',
            display: 'block',
            textAlign: 'left',
        }}
    >
        {label}
    </Typography>
);

/** Shared layout so card titles and bodies align flush left and inner rows use full width. */
const CARD_SX = {
    p: 1.5,
    borderRadius: 2,
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    boxSizing: 'border-box' as const,
};

export const MCUserProfileSection = () => {
    const { appTheme, factor, userData, setUserData } = useMCContext();
    const api = CreateApi();

    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [draft, setDraft] = useState<MomentUser | null>(null);

    useEffect(() => {
        if (userData && !editing) {
            setDraft(cloneUser(userData as MomentUser));
        }
    }, [userData, editing]);

    const f = factor < 1 ? factor : 1;

    const setDraftField = (field: keyof MomentUser, value: unknown) => {
        setDraft((prev) => (prev ? { ...prev, [field]: value as never } : prev));
    };

    const selectedUser = (userData || null) as MomentUser | null;

    const handleCancel = () => {
        if (selectedUser) setDraft(cloneUser(selectedUser));
        setSaveError(null);
        setEditing(false);
    };

    const formatDate = (val: unknown) => {
        if (!val) return '—';
        try {
            return new Date(val as string).toLocaleDateString('en-US', { timeZone: 'UTC' });
        } catch {
            return String(val);
        }
    };

    const toUTCDateInput = (val: unknown) => {
        if (!val) return '';
        try {
            return new Date(val as string).toISOString().slice(0, 10);
        } catch {
            return '';
        }
    };

    const handleSave = useCallback(async () => {
        if (!draft) return;
        setSaving(true);
        setSaveError(null);
        try {
            const ghinDigits = draft.ghinNumber != null && draft.ghinNumber !== '' ? String(draft.ghinNumber).trim() : '';
            const payload: Record<string, unknown> = {
                firstName: draft.firstName ?? '',
                lastName: draft.lastName ?? '',
                displayName: draft.displayName ?? '',
                phoneNumber: (draft.phoneNumber ?? '').replace(/\D/g, '').slice(0, 10),
                birthDate: draft.birthDate != null ? String(draft.birthDate).slice(0, 10) : '',
                address: draft.address ?? '',
                city: draft.city ?? '',
                state: draft.state ?? '',
                zipCode: (draft.zipCode ?? '').replace(/\D/g, '').slice(0, 5),
                gender: draft.gender ?? '',
                ghinNumber: ghinDigits,
            };
            if (!ghinDigits && draft.average18HoleScore !== undefined && draft.average18HoleScore !== null) {
                payload.average18HoleScore = draft.average18HoleScore;
            } else if (!ghinDigits) {
                payload.average18HoleScore = draft.average18HoleScore ?? null;
            }

            await api.put('/myaccount/UpdateOwnProfile', payload);

            const { data: refreshed } = await api.get<MomentUser>('/myaccount/GetOwnProfile');
            setUserData(refreshed);
            setDraft(cloneUser(refreshed));
            setEditing(false);
        } catch (err: unknown) {
            const msg =
                axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object' && err.response.data !== null && 'error' in err.response.data
                    ? String((err.response.data as { error: string }).error)
                    : 'Failed to save changes. Please try again.';
            setSaveError(msg);
        } finally {
            setSaving(false);
        }
    }, [api, draft, setUserData]);

    if (!selectedUser) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Loading profile…
                </Typography>
            </Box>
        );
    }

    if (!draft) {
        return null;
    }

    const ghinPresent = hasGhinValue(draft.ghinNumber);

    const tours = selectedUser.tours ?? [];

    return (
        <Box
            sx={{
                flex: 1,
                width: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                px: 2,
                py: 1.5,
                boxSizing: 'border-box',
            }}
        >
            <Box sx={{ flexShrink: 0, mb: 1.5, width: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                    <Typography variant="subtitle1" color="primary.main" sx={{ fontWeight: 600, fontSize: 14 * f }}>
                        Profile
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {!editing ? (
                            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)} sx={{ fontSize: 11 * f }}>
                                Edit
                            </Button>
                        ) : (
                            <>
                                <Button size="small" variant="outlined" color="inherit" startIcon={<CancelIcon />} onClick={handleCancel} disabled={saving} sx={{ fontSize: 11 * f }}>
                                    Cancel
                                </Button>
                                <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />} onClick={() => void handleSave()} disabled={saving} sx={{ fontSize: 11 * f }}>
                                    {saving ? 'Saving…' : 'Save'}
                                </Button>
                            </>
                        )}
                    </Stack>
                </Stack>
                {saveError && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                        {saveError}
                    </Alert>
                )}
            </Box>

            <Box
                sx={{
                    flex: '1 1 0',
                    height: 0,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                    width: '100%',
                    alignSelf: 'stretch',
                }}
            >
                <Stack direction="column" spacing={2} sx={{ pb: 0.5, width: '100%', alignItems: 'stretch' }}>
                    <Paper variant="outlined" sx={CARD_SX}>
                        <SectionLabel label="Identity" factor={f} />
                        <Stack direction="row" flexWrap="wrap" gap={2} mt={1} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                            <ReadOnlyField label="Email" value={selectedUser.userEmail} factor={f} />
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={CARD_SX}>
                        <SectionLabel label="Name" factor={f} />
                        <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                            {editing ? (
                                <>
                                    <StyledTextboxInput label="First Name" value={draft.firstName} onChange={(e) => setDraftField('firstName', e.target.value)} factor={f} appTheme={appTheme} sx={{ flex: 1, minWidth: 130, maxWidth: 200 }} />
                                    <StyledTextboxInput label="Last Name" value={draft.lastName} onChange={(e) => setDraftField('lastName', e.target.value)} factor={f} appTheme={appTheme} sx={{ flex: 1, minWidth: 130, maxWidth: 200 }} />
                                    <StyledTextboxInput label="Display Name" value={draft.displayName} onChange={(e) => setDraftField('displayName', e.target.value)} factor={f} appTheme={appTheme} sx={{ flex: 1, minWidth: 180, maxWidth: 320 }} />
                                </>
                            ) : (
                                <>
                                    <ReadOnlyField label="First Name" value={selectedUser.firstName} factor={f} />
                                    <ReadOnlyField label="Last Name" value={selectedUser.lastName} factor={f} />
                                    <ReadOnlyField label="Display Name" value={selectedUser.displayName} factor={f} />
                                </>
                            )}
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={CARD_SX}>
                        <SectionLabel label="Account & Membership" factor={f} />
                        <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                            <ReadOnlyField label="Membership Type" value={selectedUser.membershipType} factor={f} />
                            <ReadOnlyField label="Membership Tier" value={`Tier ${selectedUser.membershipTier ?? '—'}`} factor={f} />
                            <ReadOnlyField label="Account Type" value={selectedUser.accountType} factor={f} />
                            <ReadOnlyField label="Account Access Level" value={`Tier ${selectedUser.accountTier ?? '—'}`} factor={f} />
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={CARD_SX}>
                        <SectionLabel label="Contact Information" factor={f} />
                        <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                            {editing ? (
                                <>
                                    <StyledTextboxInput
                                        label="Phone Number"
                                        value={draft.phoneNumber}
                                        onChange={(e) => setDraftField('phoneNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                                        factor={f}
                                        appTheme={appTheme}
                                        inputProps={{ type: 'tel', autoComplete: 'tel' }}
                                        helperText={draft.phoneNumber && String(draft.phoneNumber).replace(/\D/g, '').length !== 10 ? 'Must be 10 digits' : undefined}
                                        error={!!draft.phoneNumber && String(draft.phoneNumber).replace(/\D/g, '').length !== 10}
                                        sx={{ width: 180, flexShrink: 0 }}
                                    />
                                    <StyledTextboxInput label="Birth Date" value={toUTCDateInput(draft.birthDate)} onChange={(e) => setDraftField('birthDate', e.target.value)} type="date" factor={f} appTheme={appTheme} inputProps={{ autoComplete: 'bday' }} sx={{ width: 170, flexShrink: 0 }} />
                                    <Box sx={{ width: 140, flexShrink: 0 }}>
                                        <StyledAutocompleteInput
                                            label="Gender"
                                            factor={f}
                                            appTheme={appTheme}
                                            options={GENDER_OPTIONS}
                                            width={140}
                                            value={draft.gender && GENDER_OPTIONS.includes(draft.gender) ? draft.gender : null}
                                            getOptionLabel={(o: string) => o}
                                            isOptionEqualToValue={(o: string, v: string) => o === v}
                                            onChange={(_: unknown, val: string | null) => setDraftField('gender', val ?? '')}
                                            renderInput={(params) => <StyledTextboxInput {...params} />}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <ReadOnlyField label="Phone Number" value={selectedUser.phoneNumber ? selectedUser.phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') : '—'} factor={f} />
                                    <ReadOnlyField label="Birth Date" value={formatDate(selectedUser.birthDate)} factor={f} />
                                    <ReadOnlyField label="Gender" value={selectedUser.gender} factor={f} />
                                </>
                            )}
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={CARD_SX}>
                        <SectionLabel label="Address" factor={f} />
                        <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                            {editing ? (
                                <>
                                    <StyledTextboxInput label="Street Address" value={draft.address} onChange={(e) => setDraftField('address', e.target.value)} factor={f} appTheme={appTheme} inputProps={{ autoComplete: 'street-address' }} sx={{ flex: 2, minWidth: 200, maxWidth: '50%' }} />
                                    <StyledTextboxInput label="City" value={draft.city} onChange={(e) => setDraftField('city', e.target.value)} factor={f} appTheme={appTheme} inputProps={{ autoComplete: 'address-level2' }} sx={{ flex: 1, minWidth: 100 }} />
                                    <Box sx={{ flexShrink: 0, width: 90 }}>
                                        <StyledAutocompleteInput
                                            label="State"
                                            factor={f}
                                            appTheme={appTheme}
                                            options={stateAbbreviations.states}
                                            value={draft.state || null}
                                            getOptionLabel={(o: string) => o}
                                            isOptionEqualToValue={(o: string, v: string) => o === v}
                                            onChange={(_: unknown, val: string | null) => setDraftField('state', val ?? '')}
                                            autoHighlight
                                            renderInput={(params) => <StyledTextboxInput {...params} />}
                                        />
                                    </Box>
                                    <StyledTextboxInput
                                        label="Zip Code"
                                        value={draft.zipCode}
                                        onChange={(e) => setDraftField('zipCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                                        factor={f}
                                        appTheme={appTheme}
                                        error={!!draft.zipCode && String(draft.zipCode).replace(/\D/g, '').length !== 5}
                                        helperText={draft.zipCode && String(draft.zipCode).replace(/\D/g, '').length !== 5 ? 'Must be 5 digits' : undefined}
                                        inputProps={{ autoComplete: 'postal-code' }}
                                        sx={{ width: 110, flexShrink: 0 }}
                                    />
                                </>
                            ) : (
                                <>
                                    <ReadOnlyField label="Street" value={selectedUser.address} factor={f} />
                                    <ReadOnlyField label="City" value={selectedUser.city} factor={f} />
                                    <ReadOnlyField label="State" value={selectedUser.state} factor={f} />
                                    <ReadOnlyField label="Zip Code" value={selectedUser.zipCode} factor={f} />
                                </>
                            )}
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={CARD_SX}>
                        <SectionLabel label="Golf Information" factor={f} />
                        <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                            {editing ? (
                                <>
                                    <StyledTextboxInput label="GHIN Number" value={draft.ghinNumber} onChange={(e) => setDraftField('ghinNumber', e.target.value.trim())} factor={f} appTheme={appTheme} sx={{ width: 180, flexShrink: 0 }} />
                                    {!ghinPresent ? (
                                        <StyledTextboxInput
                                            label="Average 18 Hole Score"
                                            value={draft.average18HoleScore != null ? String(draft.average18HoleScore) : ''}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === '') setDraftField('average18HoleScore', null);
                                                else {
                                                    const n = Number(v);
                                                    setDraftField('average18HoleScore', Number.isNaN(n) ? null : n);
                                                }
                                            }}
                                            type="number"
                                            factor={f}
                                            appTheme={appTheme}
                                            inputProps={{ min: 40, max: 144 }}
                                            sx={{ width: 180, flexShrink: 0 }}
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <ReadOnlyField label="GHIN Number" value={selectedUser.ghinNumber ? String(selectedUser.ghinNumber) : ''} factor={f} />
                                    {!hasGhinValue(selectedUser.ghinNumber) ? (
                                        <ReadOnlyField label="Average 18 Hole Score" value={selectedUser.average18HoleScore != null ? String(selectedUser.average18HoleScore) : ''} factor={f} />
                                    ) : null}
                                </>
                            )}
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={CARD_SX}>
                        <Stack direction="row" alignItems="center" spacing={0.75} mb={1} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                            <EmojiEventsIcon color="primary" sx={{ fontSize: 16 }} />
                            <Typography
                                variant="caption"
                                color="primary.main"
                                sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: 1,
                                    fontSize: 10 * f,
                                    textAlign: 'left',
                                }}
                            >
                                Series
                            </Typography>
                        </Stack>
                        {tours.length === 0 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 * f, textAlign: 'left', width: '100%' }}>
                                No series associations on record.
                            </Typography>
                        ) : (
                            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ width: '100%', justifyContent: 'flex-start' }}>
                                {tours.map((t) => (
                                    <Chip key={t.tourID} label={t.tourName || t.tourID} size="small" color="primary" variant="outlined" sx={{ fontSize: 11 * f }} />
                                ))}
                            </Stack>
                        )}
                    </Paper>
                </Stack>
            </Box>
        </Box>
    );
};
